/**
 * Table Read — plays a screenplay aloud with a cast of voices and a narrator.
 *
 * Setup lives in a right-side drawer (what to read + cast & voices). Once a read
 * starts, a full-width player bar docks to the bottom of the screen (standard
 * audio-player feel): now-playing, transport, speed, and a clickable progress
 * bar with a tick per scene. The read position is remembered per script, so the
 * writer can pause, close the panel, or reload and pick up where they left off.
 * If a line won't generate, the player pauses on it (Retry / Skip) instead of
 * ending the whole read.
 *
 * Brand: never says "AI" — it's a "Table Read", a "Cast", a "Narrator".
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, SkipForward, SkipBack, Square, X, Users, Volume2, Loader2, Mic, RefreshCw, RotateCcw } from "lucide-react";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function TableReadPanel({ scriptId, elements, canGenerate, open, onClose }) {
  const [scope, setScope] = useState("scene");
  const [pickSceneId, setPickSceneId] = useState("current");
  const [cast, setCast] = useState(null);
  const [castOpen, setCastOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | preparing | playing | paused | stalled | done
  const [idx, setIdx] = useState(-1);
  const [speed, setSpeed] = useState(1);
  const [loadingSegs, setLoadingSegs] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [matching, setMatching] = useState(false);
  const [resume, setResume] = useState(null); // saved resume point for the drawer affordance
  const autoMatchedRef = useRef(false);

  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const barTrackRef = useRef(null);
  const urlCache = useRef(new Map());     // hash -> objectURL
  const promiseCache = useRef(new Map()); // hash -> Promise<objectURL>
  const playing = useRef(false);
  const idxRef = useRef(-1);
  const segRef = useRef([]);
  const speedRef = useRef(1);
  const scopeRef = useRef("scene");
  const sceneIdRef = useRef(null);
  const token = useRef(0);

  const elementsRef = useRef(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);
  useEffect(() => { scopeRef.current = scope; }, [scope]);

  // ---- resume persistence (per script) ----
  const RESUME_KEY = `tableread:resume:${scriptId}`;
  const loadResume = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(RESUME_KEY) || "null"); } catch { return null; }
  }, [RESUME_KEY]);
  const saveResume = useCallback((data) => {
    try { localStorage.setItem(RESUME_KEY, JSON.stringify(data)); } catch { /* storage blocked */ }
  }, [RESUME_KEY]);
  const clearResumeStore = useCallback(() => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
  }, [RESUME_KEY]);

  // ---- cast ----
  const loadCast = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/read/cast`);
      setCast(data);
    } catch { /* ignore */ }
  }, [scriptId]);

  useEffect(() => { if (open && scriptId) loadCast(); }, [open, scriptId, loadCast]);

  const voiceLabel = (id) => (cast?.voices || []).find((v) => v.id === id)?.label || id;

  const updateNarrator = async (voice) => {
    setCast((c) => ({ ...c, narrator_voice: voice }));
    try { await api.put(`/scripts/${scriptId}/read/cast`, { narrator_voice: voice }); }
    catch { toast.error("Couldn't save the narrator voice. Please try again."); }
  };
  const updateVoice = async (charId, voice) => {
    setCast((c) => ({
      ...c,
      characters: c.characters.map((ch) =>
        (ch.id === charId ? { ...ch, voice, voice_label: voiceLabel(voice), is_auto: false, stale: false, needs_match: false } : ch)),
    }));
    try { await api.put(`/scripts/${scriptId}/read/cast`, { assignments: { [charId]: voice } }); }
    catch { toast.error("Couldn't save that voice. Please try again."); }
  };

  const autoMatch = useCallback(async (payload) => {
    setMatching(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/read/cast/automatch`, payload);
      setCast((c) => ({ ...c, characters: data.characters }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't match voices to profiles.");
    } finally { setMatching(false); }
  }, [scriptId]);
  const reMatchAll = () => autoMatch({ scope: "all" });
  const reCastOne = (id) => autoMatch({ character_ids: [id] });

  // Auto-match any not-yet-cast character once, when the cast section opens.
  useEffect(() => {
    if (castOpen && cast && !autoMatchedRef.current && (cast.characters || []).some((c) => c.needs_match)) {
      autoMatchedRef.current = true;
      autoMatch({ scope: "missing" });
    }
  }, [castOpen, cast, autoMatch]);

  // ---- editor highlight / follow-along ----
  const clearHighlight = useCallback(() => {
    const prev = document.querySelector(".tr-reading");
    if (prev) prev.classList.remove("tr-reading");
  }, []);
  const highlight = useCallback((elementId) => {
    clearHighlight();
    const node = document.querySelector(`[data-element-id="${elementId}"]`);
    if (node) {
      node.classList.add("tr-reading");
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [clearHighlight]);

  const getCurrentSceneId = () => {
    const headings = (elementsRef.current || []).filter((e) => e.type === "scene_heading");
    if (!headings.length) return null;
    let current = headings[0].id;
    for (const h of headings) {
      const node = document.querySelector(`[data-element-id="${h.id}"]`);
      if (node && node.getBoundingClientRect().top <= 180) current = h.id;
    }
    return current;
  };

  // ---- clip fetch (with one auto-retry for transient failures) ----
  const ensureClipUrl = useCallback((seg) => {
    const h = seg.hash;
    if (urlCache.current.has(h)) return Promise.resolve(urlCache.current.get(h));
    if (promiseCache.current.has(h)) return promiseCache.current.get(h);
    const p = (async () => {
      let lastErr;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          if (!seg.ready) {
            await api.post(`/scripts/${scriptId}/read/clip`, { text: seg.text, voice: seg.voice });
            seg.ready = true;
          }
          const resp = await api.get(`/scripts/${scriptId}/read/clip/${h}`, { responseType: "blob" });
          const url = URL.createObjectURL(resp.data);
          urlCache.current.set(h, url);
          promiseCache.current.delete(h);
          return url;
        } catch (e) {
          lastErr = e; seg.ready = false;
          if (attempt === 0) await sleep(700);
        }
      }
      throw lastErr;
    })().catch((e) => { promiseCache.current.delete(h); throw e; });
    promiseCache.current.set(h, p);
    return p;
  }, [scriptId]);

  const finish = useCallback(() => {
    playing.current = false;
    setStatus("done");
    clearResumeStore();
    setResume(null);
  }, [clearResumeStore]);

  const playFrom = useCallback(async (start, tk) => {
    const segs = segRef.current;
    if (tk !== token.current) return;
    let i = start < 0 ? 0 : start;
    if (i >= segs.length) { clearHighlight(); finish(); return; }
    idxRef.current = i; setIdx(i);
    const seg = segs[i];
    highlight(seg.elementId);
    saveResume({ scope: scopeRef.current, sceneId: sceneIdRef.current, idx: i, elementId: seg.elementId, n: segs.length, ts: Date.now() });
    // prefetch a small look-ahead buffer for gapless playback
    for (let k = i; k < Math.min(i + 3, segs.length); k++) ensureClipUrl(segs[k]).catch(() => {});
    const cached = urlCache.current.has(seg.hash);
    setStatus(cached ? "playing" : "preparing");
    let url;
    try { url = await ensureClipUrl(seg); }
    catch {
      if (tk === token.current) { playing.current = false; setStatus("stalled"); }
      return;
    }
    if (tk !== token.current || !playing.current) return;
    setStatus("playing");
    const audio = audioRef.current;
    audio.src = url;
    audio.playbackRate = speedRef.current;
    audio.onended = () => { if (tk === token.current && playing.current) playFrom(idxRef.current + 1, tk); };
    try { await audio.play(); } catch { /* interrupted */ }
  }, [ensureClipUrl, finish, highlight, clearHighlight, saveResume]);

  const fetchSegments = useCallback(async (scp, sceneId) => {
    setLoadingSegs(true);
    try {
      const params = scp === "all" ? { scope: "all" } : { scope: scp, scene_id: sceneId };
      const { data } = await api.get(`/scripts/${scriptId}/read/segments`, { params });
      return data.segments || [];
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the read.");
      return null;
    } finally { setLoadingSegs(false); }
  }, [scriptId]);

  const resolveSceneId = useCallback(() =>
    (pickSceneId === "current" ? getCurrentSceneId() : pickSceneId), [pickSceneId]);

  const startPlayback = useCallback(async () => {
    const sceneId = scope === "all" ? null : resolveSceneId();
    sceneIdRef.current = sceneId; scopeRef.current = scope;
    const segs = await fetchSegments(scope, sceneId);
    if (segs === null) { setStatus("idle"); return; }
    if (!segs.length) { toast.message("Nothing to read in this selection yet."); return; }
    setSegments(segs); segRef.current = segs;
    token.current += 1; playing.current = true;
    await playFrom(0, token.current);
  }, [scope, fetchSegments, playFrom, resolveSceneId]);

  const resumeRead = useCallback(async () => {
    const r = loadResume();
    if (!r) { await startPlayback(); return; }
    setScope(r.scope); scopeRef.current = r.scope; sceneIdRef.current = r.sceneId ?? null;
    const segs = await fetchSegments(r.scope, r.sceneId);
    if (segs === null) { setStatus("idle"); return; }
    if (!segs.length) { clearResumeStore(); setResume(null); toast.message("Nothing left to read here."); return; }
    setSegments(segs); segRef.current = segs;
    let i = segs.findIndex((s) => s.elementId === r.elementId);
    if (i < 0) i = Math.min(typeof r.idx === "number" ? r.idx : 0, segs.length - 1);
    token.current += 1; playing.current = true;
    await playFrom(i, token.current);
  }, [loadResume, fetchSegments, playFrom, startPlayback, clearResumeStore]);

  const pause = useCallback(() => {
    playing.current = false;
    audioRef.current?.pause();
    setStatus("paused");
  }, []);

  const togglePlay = useCallback(async () => {
    if (status === "playing") { pause(); return; }
    if (status === "paused" && audioRef.current?.src) {
      playing.current = true; setStatus("playing");
      try { await audioRef.current.play(); } catch { /* ignore */ }
      return;
    }
    if (segRef.current.length) {
      const start = status === "done" ? 0 : (idxRef.current >= 0 ? idxRef.current : 0);
      token.current += 1; playing.current = true;
      await playFrom(start, token.current);
      return;
    }
    if (loadResume()) { await resumeRead(); } else { await startPlayback(); }
  }, [status, pause, playFrom, loadResume, resumeRead, startPlayback]);

  // Stop: keep the spot (saved), collapse the player. Only "Start over" clears it.
  const stop = useCallback(() => {
    playing.current = false;
    token.current += 1;
    audioRef.current?.pause();
    clearHighlight();
    setStatus("idle"); setIdx(-1); idxRef.current = -1;
    setSegments([]); segRef.current = [];
    setResume(loadResume());
  }, [clearHighlight, loadResume]);

  const startOver = useCallback(async () => {
    const r = loadResume();
    clearResumeStore(); setResume(null);
    if (segRef.current.length) {
      token.current += 1; playing.current = true;
      await playFrom(0, token.current);
      return;
    }
    // No segments loaded (e.g. after a reload) — restore the saved selection so
    // "Start over" restarts the same scope (whole script vs. current scene).
    const scp = r?.scope || scope;
    const sceneId = r ? (r.sceneId ?? null) : (scope === "all" ? null : resolveSceneId());
    setScope(scp); scopeRef.current = scp; sceneIdRef.current = sceneId;
    const segs = await fetchSegments(scp, sceneId);
    if (segs === null) { setStatus("idle"); return; }
    if (!segs.length) { toast.message("Nothing to read in this selection yet."); return; }
    setSegments(segs); segRef.current = segs;
    token.current += 1; playing.current = true;
    await playFrom(0, token.current);
  }, [loadResume, clearResumeStore, playFrom, scope, fetchSegments, resolveSceneId]);

  const jump = useCallback(async (target) => {
    const segs = segRef.current;
    if (!segs.length) return;
    const t = Math.max(0, Math.min(target, segs.length - 1));
    token.current += 1;
    audioRef.current?.pause();
    playing.current = true;
    await playFrom(t, token.current);
  }, [playFrom]);

  const retryLine = useCallback(async () => {
    const seg = segRef.current[idxRef.current];
    if (seg) { promiseCache.current.delete(seg.hash); seg.ready = false; }
    token.current += 1; playing.current = true;
    await playFrom(idxRef.current, token.current);
  }, [playFrom]);

  const onSeek = useCallback((e) => {
    const track = barTrackRef.current;
    if (!track || !segRef.current.length) return;
    const rect = track.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    jump(Math.round(frac * (segRef.current.length - 1)));
  }, [jump]);

  const changeSpeed = (v) => {
    setSpeed(v); speedRef.current = v;
    if (audioRef.current) audioRef.current.playbackRate = v;
  };

  const preview = async (voice, sampleText, key) => {
    setPreviewing(key);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/read/clip`, { text: sampleText, voice });
      const resp = await api.get(`/scripts/${scriptId}/read/clip/${data.hash}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = previewAudioRef.current;
      a.src = url; await a.play();
    } catch { toast.error("Couldn't preview that voice."); }
    finally { setPreviewing(null); }
  };

  // Reopening the drawer refreshes the resume affordance; closing keeps playback alive.
  useEffect(() => {
    if (open) { setResume(loadResume()); }
    else { setCastOpen(false); autoMatchedRef.current = false; }
  }, [open, loadResume]);

  // Reserve room at the bottom of the canvas (via --tr-dock-h) so the bar never
  // hides the last line, and so a stacked Timeline can dock just above it.
  const barVisible = status !== "idle" && segments.length > 0;
  const barRef = useRef(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!barVisible) { root.style.setProperty("--tr-dock-h", "0px"); return; }
    const el = barRef.current;
    const set = () => root.style.setProperty("--tr-dock-h", `${el?.offsetHeight || 0}px`);
    set();
    const ro = new ResizeObserver(set);
    if (el) ro.observe(el);
    return () => { ro.disconnect(); root.style.setProperty("--tr-dock-h", "0px"); };
  }, [barVisible]);

  // Stop audio + free memory only when the editor unmounts.
  useEffect(() => () => {
    playing.current = false;
    audioRef.current?.pause();
    previewAudioRef.current?.pause();
    urlCache.current.forEach((u) => URL.revokeObjectURL(u));
    document.documentElement.style.setProperty("--tr-dock-h", "0px");
    const prev = document.querySelector(".tr-reading");
    if (prev) prev.classList.remove("tr-reading");
  }, []);

  const cur = idx >= 0 && idx < segments.length ? segments[idx] : null;
  const isPlaying = status === "playing";
  const isPreparing = status === "preparing" || loadingSegs;
  const voiceOptions = cast?.voices || [];

  const sceneOpts = (elements || [])
    .filter((e) => e.type === "scene_heading")
    .map((e, i) => ({ id: e.id, label: `${i + 1}. ${((e.text || "").trim() || "Untitled scene").slice(0, 60)}` }));

  // scene ticks for the progress bar
  const sceneIds = new Set((elements || []).filter((e) => e.type === "scene_heading").map((e) => e.id));
  let sceneCounter = 0;
  const ticks = segments.reduce((acc, s, i) => {
    if (sceneIds.has(s.elementId)) {
      sceneCounter += 1;
      acc.push({ i, scene: sceneCounter, heading: (s.text || "").slice(0, 48), pct: segments.length > 1 ? (i / (segments.length - 1)) * 100 : 0 });
    }
    return acc;
  }, []);
  const pct = segments.length > 1 && idx >= 0 ? (idx / (segments.length - 1)) * 100 : (idx >= 0 ? 0 : 0);

  const resumeInfo = (() => {
    if (!resume) return null;
    const els = elementsRef.current || [];
    let num = 0, heading = "";
    for (const el of els) {
      if (el.type === "scene_heading") { num += 1; heading = (el.text || "").trim() || `Scene ${num}`; }
      if (el.id === resume.elementId) break;
    }
    return { num: num || 1, heading: heading || "Scene 1", line: (resume.idx ?? 0) + 1 };
  })();

  return (
    <>
      {open && (
        <aside
          className="fixed top-0 right-0 z-40 h-full w-[340px] max-w-[88vw] bg-[var(--paper)] border-l border-[var(--line)] shadow-2xl flex flex-col"
          data-testid="table-read-panel"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-[var(--line)] shrink-0">
            <div className="flex items-center gap-2">
              <Mic size={16} className="text-[var(--brand)]" strokeWidth={1.75} />
              <h2 className="font-display text-lg text-[var(--ink-primary)]">Table Read</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="table-read-close" title="Close">
              <X size={16} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto" style={{ paddingBottom: barVisible ? 184 : undefined }}>
            {/* Scope */}
            <div className="px-4 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">What to read</p>
              <div className="grid grid-cols-3 gap-2">
                {[["scene", "This scene"], ["from", "From here on"], ["all", "Whole script"]].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setScope(val)}
                    disabled={barVisible}
                    className={`text-xs py-2 rounded border transition-colors ${scope === val ? "border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--ink-primary)]" : "border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"} disabled:opacity-50`}
                    data-testid={`table-read-scope-${val}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {scope !== "all" && (
                <div className="mt-2.5">
                  <label className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] block mb-1">
                    {scope === "from" ? "Start at" : "Scene"}
                  </label>
                  <select
                    value={pickSceneId}
                    onChange={(e) => setPickSceneId(e.target.value)}
                    disabled={barVisible}
                    className="w-full text-xs bg-white border border-[var(--line)] rounded px-2 py-2 outline-none focus:border-[var(--brand)] disabled:opacity-50"
                    data-testid="table-read-scene-picker"
                  >
                    <option value="current">Where I'm reading (scene in view)</option>
                    {sceneOpts.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Primary action */}
            <div className="px-4 py-4">
              {barVisible ? (
                <div className="rounded-md border border-[var(--line)] bg-[var(--canvas)] p-4 text-center" data-testid="table-read-active-hint">
                  <p className="text-sm text-[var(--ink-secondary)] mb-3">Playing in the bar below.</p>
                  <button onClick={stop} className="text-[11px] uppercase tracking-wider text-[var(--ink-muted)] hover:text-[var(--err)] inline-flex items-center gap-1.5" data-testid="table-read-stop-drawer">
                    <Square size={11} /> Stop
                  </button>
                </div>
              ) : resumeInfo ? (
                <div className="rounded-md border border-[var(--brand)]/40 bg-[var(--brand-soft)] p-4" data-testid="table-read-resume-card">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--brand)] mb-1">Pick up where you left off</p>
                  <p className="text-sm text-[var(--ink-primary)] mb-3 truncate" title={resumeInfo.heading}>
                    Scene {resumeInfo.num} · line {resumeInfo.line}
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={resumeRead} disabled={isPreparing} className="flex-1 py-2 rounded bg-[var(--brand)] text-white text-sm hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-1.5" data-testid="table-read-resume-btn">
                      {isPreparing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Resume
                    </button>
                    <button onClick={startOver} disabled={isPreparing} className="px-3 py-2 rounded border border-[var(--line)] text-sm text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] disabled:opacity-60" data-testid="table-read-startover-btn">
                      Start over
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={togglePlay} disabled={isPreparing} className="w-full py-2.5 rounded bg-[var(--brand)] text-white text-sm hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="table-read-start-btn">
                  {isPreparing ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Start read
                </button>
              )}
            </div>

            {/* Cast */}
            <div className="mt-1 border-t border-[var(--line)]">
              <button onClick={() => setCastOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--warm-accent)]" data-testid="table-read-cast-toggle">
                <span className="flex items-center gap-2 text-sm text-[var(--ink-primary)]"><Users size={14} className="text-[var(--ink-muted)]" /> Cast & voices</span>
                <span className="text-[11px] text-[var(--ink-muted)]">{castOpen ? "Hide" : "Edit"}</span>
              </button>
              {castOpen && cast && (
                <div className="px-4 pb-5" data-testid="table-read-cast">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)]" data-testid="table-read-cast-hint">
                      {matching ? "Matching voices…" : "Voices match each profile"}
                    </p>
                    <button onClick={reMatchAll} disabled={matching} className="text-[10px] uppercase tracking-wider text-[var(--brand)] hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="table-read-rematch-all">
                      {matching ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Re-match all
                    </button>
                  </div>
                  <div className="space-y-3">
                    <VoiceRow
                      label="Narrator" sub="Scene headings & action"
                      value={cast.narrator_voice} voices={voiceOptions}
                      onChange={updateNarrator} disabled={matching}
                      onPreview={() => preview(cast.narrator_voice, "And so the scene begins.", "narrator")}
                      previewing={previewing === "narrator"}
                      testid="table-read-narrator"
                    />
                    {(cast.characters || []).map((ch) => (
                      <VoiceRow
                        key={ch.id}
                        label={ch.name}
                        sub={ch.is_auto ? "Matched from profile" : "Your pick"}
                        value={ch.voice} voices={voiceOptions} disabled={matching}
                        stale={ch.stale} onReCast={() => reCastOne(ch.id)}
                        onChange={(v) => updateVoice(ch.id, v)}
                        onPreview={() => preview(ch.voice, `Hi, I'm ${ch.name}.`, ch.id)}
                        previewing={previewing === ch.id}
                        testid={`table-read-voice-${ch.id}`}
                      />
                    ))}
                    {(!cast.characters || cast.characters.length === 0) && (
                      <p className="text-xs text-[var(--ink-muted)] italic">No characters detected yet — they'll appear here as you write dialogue.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Full-width bottom player bar */}
      {barVisible && (
        <div ref={barRef} className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--paper)] border-t border-[var(--line)] shadow-[0_-4px_20px_rgba(0,0,0,0.07)]" data-testid="table-read-bar">
          {/* progress + scene ticks */}
          <div className="px-4 pt-3">
            <div
              ref={barTrackRef}
              onClick={onSeek}
              className="relative h-2 rounded-full bg-[var(--warm-accent)] cursor-pointer"
              data-testid="table-read-progress-track"
              title="Click to seek"
            >
              <div className="absolute left-0 top-0 h-full rounded-full bg-[var(--brand)]" style={{ width: `${pct}%` }} />
              {ticks.map((t) => (
                <button
                  key={`tick-${t.scene}`}
                  onClick={(e) => { e.stopPropagation(); jump(t.i); }}
                  className="absolute -top-1 w-[3px] h-4 -translate-x-1/2 bg-[var(--ink-muted)] hover:bg-[var(--brand)] hover:w-[5px] rounded transition-all"
                  style={{ left: `${t.pct}%` }}
                  title={`Scene ${t.scene}${t.heading ? `: ${t.heading}` : ""}`}
                  data-testid={`table-read-scene-tick-${t.scene}`}
                />
              ))}
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[var(--brand)] shadow ring-2 ring-white pointer-events-none" style={{ left: `${pct}%` }} />
            </div>
          </div>

          {/* now playing — full line, shown above the controls */}
          <div className="px-4 pt-2 pb-1" data-testid="table-read-now">
            {cur ? (
              <>
                <p className="text-[10px] uppercase tracking-widest text-[var(--brand)] truncate" data-testid="table-read-bar-speaker">
                  {cur.role === "narrator" ? "Narrator" : cur.name} · {voiceLabel(cur.voice)} · {idx + 1}/{segments.length}
                </p>
                <p className="text-sm text-[var(--ink-primary)] leading-snug max-h-[4.5rem] overflow-y-auto" data-testid="table-read-bar-line">{cur.text}</p>
              </>
            ) : (
              <p className="text-sm text-[var(--ink-muted)] italic">{status === "done" ? "End of read." : "Preparing…"}</p>
            )}
            {status === "stalled" && (
              <p className="text-[11px] text-[var(--err)] mt-0.5 flex items-center gap-2" data-testid="table-read-stalled">
                Couldn't load this line.
                <button onClick={retryLine} className="underline hover:no-underline" data-testid="table-read-retry-btn">Retry</button>
                <button onClick={() => jump(idxRef.current + 1)} className="underline hover:no-underline" data-testid="table-read-skip-btn">Skip</button>
              </p>
            )}
          </div>

          {/* controls */}
          <div className="px-4 pb-2.5 pt-0.5 flex items-center justify-center relative">
            {/* centered cluster — speed + transport */}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-[11px] text-[var(--ink-muted)] mr-1">
                <Volume2 size={12} />
                <select value={speed} onChange={(e) => changeSpeed(parseFloat(e.target.value))} className="bg-white border border-[var(--line)] rounded px-1.5 py-0.5 text-xs outline-none" data-testid="table-read-speed-select">
                  {[0.75, 1, 1.25, 1.5].map((v) => <option key={v} value={v}>{v}×</option>)}
                </select>
              </label>
              <button onClick={() => jump(idxRef.current - 1)} className="p-2 rounded-full hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="table-read-prev-btn" title="Previous line">
                <SkipBack size={18} />
              </button>
              <button
                onClick={togglePlay}
                disabled={loadingSegs}
                className="w-11 h-11 rounded-full bg-[var(--brand)] text-white flex items-center justify-center shadow hover:opacity-90 disabled:opacity-60"
                data-testid={isPlaying ? "table-read-pause-btn" : "table-read-play-btn"}
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPreparing ? <Loader2 size={20} className="animate-spin" /> : isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
              <button onClick={() => jump(idxRef.current + 1)} className="p-2 rounded-full hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="table-read-next-btn" title="Next line">
                <SkipForward size={18} />
              </button>
            </div>

            {/* right cluster — start over + stop */}
            <div className="absolute right-3 flex items-center gap-1.5">
              <button onClick={startOver} className="p-2 rounded-full hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)] hidden sm:inline-flex" data-testid="table-read-restart-btn" title="Start over">
                <RotateCcw size={16} />
              </button>
              <button onClick={stop} className="p-2 rounded-full hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)] hover:text-[var(--err)]" data-testid="table-read-stop-btn" title="Stop (keeps your place)">
                <Square size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      <audio ref={audioRef} className="hidden" />
      <audio ref={previewAudioRef} className="hidden" />
    </>
  );
}

const AGE_GROUPS = [["child", "Kids"], ["teen", "Teens"], ["young", "Young adults"], ["adult", "Adults"], ["mature", "Older"]];
function groupedVoiceOptions(voices) {
  const vs = voices || [];
  // Custom voices (My voices / Shared voices) come first, grouped by their `group` label.
  const customGroups = [];
  for (const v of vs) {
    if (!v.group) continue;
    let g = customGroups.find((x) => x.label === v.group);
    if (!g) { g = { label: v.group, items: [] }; customGroups.push(g); }
    g.items.push(v);
  }
  const ageGroups = AGE_GROUPS
    .map(([age, label]) => ({ label, items: vs.filter((v) => !v.group && v.age === age) }))
    .filter((g) => g.items.length);
  return [...customGroups, ...ageGroups];
}

function VoiceRow({ label, sub, value, voices, onChange, onPreview, previewing, stale, onReCast, testid, disabled }) {
  const groups = groupedVoiceOptions(voices);
  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[var(--ink-primary)] truncate">{label}</p>
          {sub ? <p className="text-[10px] text-[var(--ink-muted)] truncate">{sub}</p> : null}
        </div>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="bg-white border border-[var(--line)] rounded px-2 py-1 text-xs outline-none focus:border-[var(--brand)] max-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`${testid}-select`}
        >
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </optgroup>
          ))}
        </select>
        <button
          onClick={onPreview}
          disabled={previewing}
          className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--brand)] disabled:opacity-50"
          data-testid={`${testid}-preview`}
          title="Preview voice"
        >
          {previewing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
        </button>
      </div>
      {stale && (
        <div className="flex items-center gap-2 mt-1" data-testid={`${testid}-stale`}>
          <span className="text-[9px] uppercase tracking-wider text-[var(--warn)] bg-[var(--warn)]/15 px-1.5 py-0.5 rounded">Profile changed</span>
          <button onClick={onReCast} disabled={disabled} className="text-[10px] uppercase tracking-wider text-[var(--brand)] hover:underline flex items-center gap-1 disabled:opacity-50" data-testid={`${testid}-recast`}>
            <RefreshCw size={10} /> Re-cast
          </button>
        </div>
      )}
    </div>
  );
}
